"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Mounts children only once the wrapper is within `rootMargin` of the
 * viewport. `next/dynamic` alone only code-splits the JS into a separate
 * chunk — the component still hydrates immediately on initial render.
 * Gating on IntersectionObserver defers the actual mount (and all its
 * effects: GSAP triggers, Framer Motion springs, etc.) until the section
 * is actually about to be scrolled into view.
 */
export function useLazyMount(rootMargin = "600px") {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, shouldMount]);

  return { ref, shouldMount };
}
